/**
 * @summary     A reusable, centralised "empty-state" component.
 *              Accepts a dynamic image (user / device / sensor / data illustration)
 *              and a message ID string which is internally resolved via
 *              <FormattedMessage /> from react-intl.
 *              Bootstrap utility classes handle the base layout; all custom CSS
 *              lives in NoDataComponent.css and is scoped under the "ndc__" prefix
 *              so it never bleeds into parent or sibling styles.
 *
 * @usage
 *   import NoDataComponent from './NoDataComponent';
 *   import userIcon from '../../assets/images/no-user.svg';
 *
 *   <NoDataComponent
 *     image={userIcon}
 *     message="No_User_Available"
 *     imageAlt="No User"
 *   />
 *
 * @author      Chandan Singh
 * @created     2026-05-12
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import './NoDataComponent.css';

interface NoDataComponentProps {
  image: string;
  message: string;
  imageAlt?: string;
  subHeading?: string | null;
}

const NoDataComponent: React.FC<NoDataComponentProps> = ({
  image,
  message,
  imageAlt = 'No data available',
  subHeading,
}) => {
  return (
    <div className="ndc__wrapper">
      <div className="ndc__image-container">
        <img
          src={image}
          alt={imageAlt}
          className="ndc__image img-fluid"
        />
      </div>

      <h2 className="ndc__message">
        <FormattedMessage id={message} />
      </h2>

      {subHeading && (
        <p className="ndc__subheading">
          (
          <FormattedMessage id={subHeading} />
          )
        </p>
      )}
    </div>
  );
};

export default NoDataComponent;